/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
/* istanbul ignore file */

import Graph from './graph';

interface GraphType {
  vertices: string[];
  reverseDependencies: Record<string, string[]>;
  stripName: (name: string) => string;
  graph: Map<string, string[]>;
}

export const createScopesGraph = (scopeDeps: CF.API.Scopes.Scope[], endpointDeps: CF.API.Scopes.Endpoint[]): GraphType =>
  new Graph([...scopeDeps, ...endpointDeps]);

export const BASIC_DESCRIPTIONS = {
  App: 'App: Add and Remove Annotations on Inputs',
  Annotation: 'Annotation: Add and Remove Annotations on Inputs',
  Collaborator: 'Collaborator: Add, Remove and Patch Collaborators',
  Concept: 'Concept: Add, Remove and Patch Concept',
  Input: 'Input: Add and Remove Inputs to an App',
  Metric: 'Metric: Add, Remove and Delete Evaluations Metrics',
  Model: 'Model: Add, Remove and Train Custom Models',
  Predict: 'Predict: Predict on Public and Custom Models',
  Other: 'Other: Clarifai only (for now)',
  Search: 'Search: Search over Inputs an App',
  Vocab: 'Vocab: Add, Remove and Delete lists of Concepts',
  Workflow: 'Workflow: Add, Remove and Delete Workflows of Models',
  Task: 'Task: Add, Remove and Patch Scribe labeling Tasks',
  Collector: 'Collector: Add, Remove and Patch Input Collectors',
  Key: 'API Keys: Add, Remove and Patch API Keys',
};

export const ADVANCED_DESCRIPTIONS = {
  'Apps:Add': 'Create Apps',
  'Apps:Delete': 'Delete Apps',
  'Apps:Get': 'Read Apps',
  'Annotations:Add': 'Write Annotations',
  'Annotations:Get': 'Read Annotations',
  'Annotations:Patch': 'Edit Annotation fields',
  'Annotations:Delete': 'Delete Annotations',
  Predict: 'Predict on Public and Custom Models',
  Search: 'Search by Inputs and Concepts',
  'Inputs:Add': 'Write Inputs',
  'Inputs:Get': 'Read Inputs',
  'Inputs:Patch': 'Edit Input fields',
  'Inputs:Delete': 'Delete Inputs',
  'Collaborators:Add': 'Write Collaborators',
  'Collaborators:Get': 'Read Collaborators',
  'Collaborators:Delete': 'Delete Collaborators',
  'Collectors:Add': 'Write Collectors',
  'Collectors:Get': 'Read Collectors',
  'Collectors:Delete': 'Delete Collectors',
  'Concepts:Add': 'Write Concepts',
  'Concepts:Get': 'Read Concepts',
  'Concepts:Patch': 'Edit Concept fields',
  'Concepts:Delete': 'Delete Concepts',
  'Workflows:Add': 'Write Workflows',
  'Workflows:Get': 'Read Workflows',
  'Workflows:Patch': 'Edit Workflow fields',
  'Workflows:Delete': 'Delete Workflows',
  'Keys:Add': 'Write API Keys',
  'Keys:Get': 'Read API Keys',
  'Keys:Delete': 'Delete API Keys',
  'Metrics:Add': 'Write Metrics',
  'Metrics:Get': 'Read Metrics',
  'Models:Add': 'Write Models',
  'Models:Get': 'Read Models',
  'Models:Patch': 'Edit Model fields',
  'Models:Delete': 'Delete Models',
  'Models:Train': 'Train a Custom Model',
  'Templates:Get': 'Read Model Templates',
};

export const getScopesFromGraph = (chosenBasicScopes: string[], graph: GraphType): string[] => {
  const newScopes = chosenBasicScopes.flatMap((s) => graph.graph.get(s));
  const newScopesWithDependencies = new Set(
    newScopes.flatMap((s) => {
      const deps = graph.reverseDependencies[s];
      if (deps) {
        return [s, ...deps];
      }

      return s;
    }),
  );

  return Array.from(newScopesWithDependencies);
};

export const getScopeAndEndpoint = (scopesEntity: string[]): { scopes: string[]; endpoints: string[] } => {
  return scopesEntity.reduce(
    (acc, se) => ({
      scopes: !se.includes('/clarifai.api.V2') && !se.includes('_TOP') ? acc.scopes.concat([se]) : acc.scopes,
      endpoints: se.includes('/clarifai.api.V2') ? acc.endpoints.concat([se]) : acc.endpoints,
    }),
    { scopes: [], endpoints: [] },
  );
};

export const createBasicScopes = (scopesGraph: GraphType, descriptions = BASIC_DESCRIPTIONS): { id: string; description: string }[] => {
  const { vertices } = scopesGraph;

  return vertices
    .filter((vertex) => vertex.includes('_TOP') || vertex === 'All')
    .map((vertex) => {
      if (vertex.includes('_TOP') || vertex === 'All') {
        const strippedScopeName = vertex.replace('_TOP', '');
        const scopeDescription = descriptions[strippedScopeName] || strippedScopeName;

        return {
          id: vertex,
          description: scopeDescription,
        };
      }

      return undefined;
    })
    .filter(Boolean);
};

const compareScopeEntries = (a, b): 0 | -1 | 1 => {
  if (a.name > b.name) {
    return 1;
  }
  if (a.name < b.name) {
    return -1;
  }
  return 0;
};

export const createScopeArrays = (graph: GraphType): { name: string; id: string; description: string }[][] => {
  const { vertices } = graph;
  const scopeArrays = [];
  let groupedScopes = [];
  let previousRootScope = null;

  // @ts-ignore
  for (const [index, currVertex] of vertices.entries()) {
    const currScopeName = graph.stripName(currVertex);
    const currRootVertex = currScopeName.includes('_TOP') ? currScopeName.replace('_TOP', '') : currScopeName;

    if (index === 0) {
      groupedScopes.push({
        name: currRootVertex,
        description: '',
        id: currRootVertex,
      });
      previousRootScope = currRootVertex;
      // eslint-disable-next-line no-continue
      continue;
    }

    // Note(zeiler): this required the vertices to be sorted by their stripped name
    // which is done in graph.getVertices()
    if (previousRootScope !== currRootVertex) {
      scopeArrays.push(groupedScopes);
      groupedScopes = [];
    }
    const scopeEntry = {
      name: currVertex.includes('_TOP') ? currRootVertex : currVertex,
      description: currVertex.includes('_TOP') ? '' : ADVANCED_DESCRIPTIONS[currVertex],
      id: currVertex,
      moreInfo: undefined,
      requires: undefined,
    };

    if (graph.reverseDependencies[currVertex]) {
      scopeEntry.moreInfo = true;
      scopeEntry.requires = graph.reverseDependencies[currVertex];
    }

    groupedScopes.push(scopeEntry);
    previousRootScope = currRootVertex;
  }

  scopeArrays.push(groupedScopes);

  // Ensure that top-level helpers are always at the beginning
  // of the scope array, since we render our checkboxes that order
  scopeArrays.forEach((scopeArray) => {
    scopeArray.forEach((entry, index) => {
      if (entry.id.includes('_TOP')) {
        const temp = scopeArray[index];
        scopeArray[index] = scopeArray[0];
        scopeArray[0] = temp;
      }
    });
  });

  // outside of the _TOP entry (which is now first) we want the rest sorted by name.
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < scopeArrays.length; i++) {
    // Get the rest of the array (not including _TOP which is index 0) and sort it.
    let subNamesSorted = scopeArrays[i].slice(1, scopeArrays[i].length).sort(compareScopeEntries);
    subNamesSorted = [scopeArrays[i][0], ...subNamesSorted];
    scopeArrays[i] = subNamesSorted;
  }

  return scopeArrays as unknown as { name: string; id: string; description: string }[][];
};
